<template>
  <div>
    <div>
      <b-card-actions
        title="Filters"
        action-collapse
        collapsed
      >
        <b-row>
          <b-col
            cols="12"
            md="3"
          >
            <b-form-group
              label="Requested Date"
              label-for="v-ref-no"
            >
              <div class="position-relative">
                <AdvancedDateFilter
                  :date-value="filters['created_at']"
                  :opens="'right'"
                  :clear-icon="false"
                  @update="updateDateRange('created_at', $event)"
                />
              </div>
            </b-form-group>
          </b-col>
          <b-col
            cols="12"
            md="3"
          >
            <b-form-group
              label="Status"
              label-for="status"
            >
              <v-select
                v-model="filters['status']"
                :options="queueStatusOptions"
                :reduce="option => option.value"
                label="name"
                placeholder="Select Status"
              >
                <template v-slot:option="option">
                  {{ option.name }}
                </template>
              </v-select>
            </b-form-group>
          </b-col>
        </b-row>
        <!-- Apply and Clear Filters -->
        <b-row class="mt-1">
          <b-col>
            <filter-buttons
              :filter-data="filters"
              :apply-filters="applyFilters"
              :clear-filters="clearFilters"
            />
          </b-col>
        </b-row>
      </b-card-actions>
    </div>
    <b-card no-body>
      <div>
        <b-col class="d-flex justify-content-end align-items-center my-1">
          <b-button
            v-ripple.400="'rgba(113, 102, 240, 0.15)'"
            v-b-tooltip.hover.v-primary="'Refresh Table'"
            class="custom-button-color mr-1"
            size="sm"
            @click="onClickRefresh()"
          >
            <img
              src="data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyMCIgaGVpZ2h0PSIyMCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9IiNmZmZmZmYiIHN0cm9rZS13aWR0aD0iMiIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIiBjbGFzcz0ibHVjaWRlIGx1Y2lkZS1yZWZyZXNoLWNjdyI+PHBhdGggZD0iTTIxIDEyYTkgOSAwIDAgMC05LTkgOS43NSA5Ljc1IDAgMCAwLTYuNzQgMi43NEwzIDgiLz48cGF0aCBkPSJNMyAzdjVoNSIvPjxwYXRoIGQ9Ik0zIDEyYTkgOSAwIDAgMCA5IDkgOS43NSA5Ljc1IDAgMCAwIDYuNzQtMi43NEwyMSAxNiIvPjxwYXRoIGQ9Ik0xNiAxNmg1djUiLz48L3N2Zz4="
            >
          </b-button>
        </b-col>
        <!-- table -->
        <b-overlay
          id="overlay-background"
          variant="light"
          opacity="0.30"
          blur="10px"
          rounded="sm"
        >
          <b-table
            striped
            hover
            responsive
            :per-page="perPage"
            :items="rows"
            :fields="fields"
            :sort-by.sync="sortBy"
            :sort-desc.sync="sortDesc"
            :sort-direction="sortDirection"
            show-empty
            sticky-header="100vh"
            @sort-changed="sortChanged"
          >
            <template #empty>
              <TableDataFetching
                :rows="rows"
                :data-loading="dataLoading"
              />
            </template>
            <template #head()="{ field }">
              <div
                class="sortable-header"
              >
                <span class="header-text">{{ field.label }}</span>
                <feather-icon
                  v-if="field.sortable"
                  :icon="sort === field.key
                    ? 'ArrowUpIcon'
                    : 'ArrowDownIcon'"
                  size="14"
                />
              </div>
            </template>
            <template #cell(created_at)="data">
              <div>
                {{ __dateTimeFormatter(data.item.created_at) }}
              </div>
            </template>
            <template #cell(updated_at)="data">
              <div>
                {{ __dateTimeFormatter(data.item.updated_at) }}
              </div>
            </template>
            <template #cell(filters)="data">
              <div
                v-if="data.value"
                class="py-50"
              >
                <div
                  v-for="(val, key) in parseFilters(data.value)"
                  :key="key"
                  class="mb-25 d-flex align-items-center"
                  style="font-size: 0.85rem; line-height: 1.5;"
                >
                  <!-- Icon and Label -->
                  <div class="mr-50 d-flex align-items-center text-secondary">
                    <feather-icon
                      :icon="getFilterIcon(key)"
                      size="14"
                      :style="{ color: getIconColor(key) }"
                      class="mr-50"
                    />
                    <span
                      class="font-weight-bold"
                      style="white-space: nowrap;"
                    >{{ getLabel(key) }} :</span>
                  </div>

                  <!-- Values Logic -->
                  <div class="d-flex align-items-center flex-wrap">
                    <template v-if="key === 'status' && getStatusFilterEntries(val).length">
                      <!-- Show only first 2 statuses -->
                      <span
                        v-for="status in getStatusFilterEntries(val).slice(0, 2)"
                        :key="status.key"
                        class="mr-25 my-25"
                      >
                        <b-badge
                          class="status-filter-badge"
                          :style="{
                            backgroundColor: getLightBackground(status.color),
                            color: status.color,
                            fontSize: '0.75rem',
                            padding: '0.4rem 0.6rem'
                          }"
                        >
                          {{ status.name.toUpperCase() }}
                        </b-badge>
                      </span>

                      <!-- + more tag -->
                      <b-badge
                        v-if="getStatusFilterEntries(val).length > 2"
                        variant="light-secondary"
                        class="cursor-pointer ml-25"
                        style="font-size: 0.75rem; padding: 0.4rem 0.6rem"
                        @click="openStatusModal(getStatusFilterEntries(val))"
                      >
                        + more
                      </b-badge>
                    </template>

                    <!-- Default rendering for other filters -->
                    <span
                      v-else
                      class="text-dark font-weight-bolder"
                    >
                      {{ renderFilterValue(key, val, parseFilters(data.value)) }}
                    </span>
                  </div>
                </div>
              </div>
              <span
                v-else
                class="text-muted small"
              >No filters</span>
            </template>
            <template #cell(url)="data">
              <b-button
                v-if="data.item.url"
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                variant="primary"
                size="sm"
                :href="data.item.url"
                target="_blank"
                download
              >
                <feather-icon
                  icon="DownloadIcon"
                  class="mr-50"
                />
                <span class="align-middle">Download</span>
              </b-button>
              <span v-else>No Report</span>
            </template>
            <template #cell(status)="data">
              <b-badge :variant="data.value === 'completed' ? 'light-success' : data.value === 'failed' ? 'light-danger' : 'light-info'">
                {{ data.value.toUpperCase() }}
              </b-badge>
            </template>
            <template #cell(action)="data">
              <b-dropdown
                variant="link"
                toggle-class="text-decoration-none"
                no-caret
              >
                <template v-slot:button-content>
                  <feather-icon
                    icon="MoreVerticalIcon"
                    size="12"
                    class="text-body align-middle mr-25"
                  />
                </template>
                <b-dropdown-item @click="onClickShowMerchant(data.item.average)">
                  <feather-icon icon="EyeIcon" />
                  View Merchant
                </b-dropdown-item>
              </b-dropdown>
            </template>
          </b-table>
          <div class="mx-2 mb-2">
            <b-row cols="12">
              <b-col
                class="d-flex align-items-center justify-content-sm-start"
                md="3"
              >
                <label class="width-75">Per page</label>
                <v-select
                  v-model="perPage"
                  :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                  :options="pageOptions"
                  :clearable="false"
                  class="per-page-selector d-inline-block width-125"
                />
              </b-col>
              <!-- Pagination -->
              <b-col
                class="d-flex align-items-center justify-content-sm-end"
                md="9"
              >
                <span class="text-muted pagination-text mt-1 mt-md-0 mr-1">
                  Showing {{ meta.from }} to {{ meta.to }} of {{ meta.total }} entries
                </span>
                <b-pagination
                  v-model="page"
                  :per-page="perPage"
                  :total-rows="meta.total || 0"
                  class="mb-0 mt-1 mt-sm-0"
                  first-number
                  last-number
                  next-class="next-item"
                  prev-class="prev-item"
                >
                  <template #prev-text>
                    <feather-icon
                      icon="ChevronLeftIcon"
                      size="18"
                    />
                  </template>
                  <template #next-text>
                    <feather-icon
                      icon="ChevronRightIcon"
                      size="18"
                    />
                  </template>
                </b-pagination>
              </b-col>
            </b-row>
          </div>
        </b-overlay>
      </div>
    </b-card>
    <aging-merchant ref="agingMerchant" />
    <status-filter-modal
      ref="statusFilterModal"
      :statuses="selectedStatusesForModal"
    />
  </div>
</template>

<script>
import {
  BCard,
  BCol,
  BPagination,
  BRow,
  BOverlay,
  VBModal,
  BTable,
  BDropdown,
  BDropdownItem,
  BButton,
  BBadge,
  BFormGroup,
  VBTooltip,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import vSelect from 'vue-select'
// import { TimeAgo } from 'vue2-timeago'
import 'vue2-timeago/dist/vue2-timeago.css'
import BCardActions from '@core/components/b-card-actions/BCardActions.vue'
import Filter from '@core/mixins/filter'
import AdvancedDateFilter from '@/components/AdvancedDateFilter/AdvancedDateFilter.vue'
import FilterButtons from '@/components/Filter/ReportFilterButtons.vue'
import { RepositoryFactory } from '@/repository/RepositoryFactory'
import ErrorMessage from '@core/mixins/ErrorMessage'
import StatusMixin from '@core/mixins/status'
import tableMixin from '@core/mixins/tableMixin'
import StatusFilterModal from './StatusFilterModal.vue'

const reportRepository = RepositoryFactory.get('report')
const orderRepository = RepositoryFactory.get('order')
const ResourceRepository = RepositoryFactory.get('resource')
const merchantRepository = RepositoryFactory.get('merchant')
const riderRepository = RepositoryFactory.get('rider')
const WalkingCustomerRepository = RepositoryFactory.get('walkingCustomer')

export default {
  directives: {
    'b-modal': VBModal,
    'b-tooltip': VBTooltip,
    Ripple,
  },
  components: {
    BPagination,
    BCard,
    BRow,
    BCol,
    BTable,
    BOverlay,
    vSelect,
    BDropdown,
    BDropdownItem,
    // TimeAgo,
    BButton,
    BBadge,
    BFormGroup,
    BCardActions,
    AdvancedDateFilter,
    FilterButtons,
    StatusFilterModal,
  },
  mixins: [ErrorMessage, Filter, StatusMixin, tableMixin],
  props: {
    downloadSource: {
      type: String,
      default: '',
    },
    queueReportType: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      refreshedDate: new Date(),
      longString: true,
      rows: [],
      fields: [
        {
          key: 'job_id', label: 'Report Reference', sortable: true, thClass: 'text-left', tdClass: 'text-left',
        },
        {
          key: 'created_at', label: 'Requested At', sortable: true, thClass: 'text-left', tdClass: 'text-left',
        },
        {
          key: 'updated_at', label: 'Generated At', sortable: true, thClass: 'text-left', tdClass: 'text-left',
        },
        {
          key: 'filters', label: 'Filters', thClass: 'text-left', tdClass: 'text-left',
        },
        {
          key: 'status', label: 'Status',
        },
        {
          key: 'url', label: 'Report', thClass: 'text-left', tdClass: 'text-left', thStyle: { width: '200px' },
        },
      ],
      perPage: 20,
      pageOptions: [10, 20, 50, 100, 200],
      page: 1,
      meta: {},
      dataLoading: false,
      loading: false,
      items: [],
      json_fields: {
        Average: {
          field: 'average',
          callback: value => `"${value}"`,
        },
        'Merchant Count': {
          field: 'count',
          callback: value => `"${value}"`,
        },
        'Color Code': {
          field: 'color_code',
          callback: value => `"${value}"`,
        },
      },
      queueStatusOptions: [
        { value: 'completed', name: 'Completed' },
        { value: 'failed', name: 'Failed' },
        { value: 'pending', name: 'Pending' },
      ],
      merchantBusinessMap: {},
      statusOptions: [],
      warehouseOptions: [],
      cityOptions: [],
      merchantOptions: [],
      merchantBusinessOptions: [],
      riderOptions: [],
      walkingCustomers: [],
      flagOptions: [],
      filters: {
        created_at: {
          startDate: null,
          endDate: null,
        },
      },

      // The mapping of keys to Labels
      filterLabels: {
        status: 'Status',
        customer_name: 'Customer Name',
        origin_warehouse: 'Origin Warehouse',
        destination_warehouse: 'Destination Warehouse',
        origin_city_id: 'Origin City',
        destination_city_id: 'Destination City',
        customer_phone: 'Customer Mobile',
        'orders.weight': 'Weight',
        'orders.created_at': 'Order Date',
        merchant_id: 'Merchant',
        merchant_business_id: 'Merchant Business',
        rider: 'Rider',
        'staff.id': 'Rider',
        status_date: 'Status Date',
        'orders.cod': 'COD',
        'orders.collected_cod': 'Collected COD',
        'orders.delivery_charge': 'Delivery Charge',
        'orders.temporary_warehouse_id': 'Temp Warehouse',
        walking_customer_id: 'Walking Customer',
        order_flag: 'Order Flags',
        delivery_attempts: 'Delivery Attempts',
        'app_warehouses.id': 'Warehouse',
        current_warehouse: 'Current Warehouse',
        'order_status_key_5.created_at': 'Assign Date',
        'order_status_key_9.created_at': 'Received Date',
        'order_status_key_12.created_at': 'Assign Date',
        'osk10.created_at': 'Dispatched Date',
        'osk19.created_at': 'Returned Date',
        'osk20.created_at': 'Returned Date',
        'osk27.created_at': 'Returned Date',
        'order_reschedules.created_at': 'Rescheduled Date',
      },
      selectedStatusesForModal: [],
      sort: '',
      sortBy: '',
      sortDesc: false,
      sortDirection: 'asc',
    }
  },
  computed: {
    isAssignedOrderDownload() {
      return (this.downloadSource || this.$route.params.key) === 'assigned-order'
    },
  },
  watch: {
    page() {
      this.fetchReportDownloads()
    },
    perPage() {
      this.fetchReportDownloads()
    },
    queueReportType() {
      this.page = 1
      this.fetchReportDownloads()
    },
  },
  mounted() {
    this.readOrderStatuses()
    this.getWarehouseList()
    this.getCityList()
    this.getMerchantList()
    this.getRiderList()
    this.getWalkingCustomerList()
    this.getFlagList()
    this.fetchReportDownloads()
  },
  methods: {
    async readOrderStatuses() {
      this.statusOptions = localStorage.getItem('order_statuses') ? JSON.parse(localStorage.getItem('order_statuses')) : (await orderRepository.getFullStatusList()).data.data
      if (!localStorage.getItem('order_statuses')) localStorage.setItem('order_statuses', JSON.stringify(this.statusOptions))
    },
    async getWarehouseList() {
      try {
        this.warehouseOptions = localStorage.getItem('warehouse_list') ? JSON.parse(localStorage.getItem('warehouse_list')) : (await ResourceRepository.getWarehouseListForDropDown()).data.data
        if (!localStorage.getItem('warehouse_list')) localStorage.setItem('warehouse_list', JSON.stringify(this.warehouseOptions))
      } catch (e) {
        this.convertAndNotifyError(e)
      }
    },
    async getCityList() {
      try {
        this.cityOptions = localStorage.getItem('city_list') ? JSON.parse(localStorage.getItem('city_list')) : (await ResourceRepository.getCityDropdownListNoFilter()).data.data
        if (!localStorage.getItem('city_list')) localStorage.setItem('city_list', JSON.stringify(this.cityOptions))
      } catch (e) {
        this.convertAndNotifyError(e)
      }
    },
    async getMerchantList() {
      this.merchantOptions = localStorage.getItem('merchant_list') ? JSON.parse(localStorage.getItem('merchant_list')) : (await merchantRepository.getMerchantListForDropdown()).data.data
      if (!localStorage.getItem('merchant_list')) localStorage.setItem('merchant_list', JSON.stringify(this.merchantOptions))
    },
    async getRiderList() {
      this.riderOptions = localStorage.getItem('rider_list') ? JSON.parse(localStorage.getItem('rider_list')) : (await riderRepository.getAllRiderDropDown()).data.data
      if (!localStorage.getItem('rider_list')) localStorage.setItem('rider_list', JSON.stringify(this.riderOptions))
    },
    async getWalkingCustomerList() {
      this.walkingCustomers = localStorage.getItem('walking_customer_list') ? JSON.parse(localStorage.getItem('walking_customer_list')) : (await WalkingCustomerRepository.getWalkingCustomerDropdownList()).data.data
      if (!localStorage.getItem('walking_customer_list')) localStorage.setItem('walking_customer_list', JSON.stringify(this.walkingCustomers))
    },
    async getFlagList() {
      this.flagOptions = localStorage.getItem('flag_list') ? JSON.parse(localStorage.getItem('flag_list')) : (await ResourceRepository.getFlagList()).data.data
      if (!localStorage.getItem('flag_list')) localStorage.setItem('flag_list', JSON.stringify(this.flagOptions))
    },
    async fetchReportDownloads() {
      if (this.isAssignedOrderDownload && !this.queueReportType) {
        this.rows = []
        this.meta = {}
        return
      }
      this.dataLoading = true
      this.rows = []
      const filterQuery = this.buildFilterQuery()
      try {
        const reportType = this.downloadSource || this.$route.params.key
        let data
        if (reportType === 'merchant-invoice') {
          const response = await reportRepository.fetchInvoiceReportDownloads(filterQuery, this.page, this.perPage)
          data = response.data
        } else if (reportType === 'assigned-order') {
          const response = await reportRepository.fetchAssignedOrderReportDownloads(filterQuery, this.page, this.perPage, this.queueReportType)
          data = response.data
        } else {
          const response = await reportRepository.fetchReportDownloads(filterQuery, this.page, this.perPage)
          data = response.data
        }
        this.rows = data.data
        this.meta = data.meta
        this.fetchDependentBusinesses(this.rows)
      } catch (e) {
        this.convertAndNotifyError(e)
      }
      this.dataLoading = false
    },
    // eslint-disable-next-line consistent-return
    async fetchReportDownloadsNoPagination() {
      try {
        const reportType = this.downloadSource || this.$route.params.key
        const filterQuery = this.buildFilterQuery()
        let data
        if (reportType === 'merchant-invoice') {
          const response = await reportRepository.fetchInvoiceReportDownloadsNoPagination(filterQuery)
          data = response.data.data
        } else if (reportType === 'assigned-order') {
          const response = await reportRepository.fetchAssignedOrderReportDownloadsNoPagination(filterQuery, this.queueReportType)
          data = response.data.data
        } else {
          const response = await reportRepository.fetchReportDownloadsNoPagination(filterQuery)
          data = response.data.data
        }
        return data
      } catch (e) {
        this.convertAndNotifyError(e)
      }
    },
    fetchDependentBusinesses(rows) {
      rows.forEach(row => {
        const filters = this.parseFilters(row.filters)
        const mId = filters.merchant_id

        // If there is a business filter but we haven't loaded that merchant's businesses yet
        if (filters.merchant_business_id && mId && !this.merchantBusinessMap[mId]) {
          this.fetchBusinessesForMerchant(mId)
        }
      })
    },

    async fetchBusinessesForMerchant(merchantId) {
      try {
      // Avoid duplicate requests if multiple rows have the same merchant
        if (this.merchantBusinessMap[merchantId]) return

        // Use your repository to fetch by merchantId
        // Ensure you initialize the key immediately to prevent race conditions
        this.$set(this.merchantBusinessMap, merchantId, [])

        const { data } = (await merchantRepository.getMerchantBusinessListDropdown(merchantId)).data
        this.$set(this.merchantBusinessMap, merchantId, data)
      } catch (e) {
        this.convertAndNotifyError(e)
      }
    },
    // refreshAgingReport() {
    //   this.loading = true
    //   this.refreshedDate = new Date()
    //   this.fetchAgingReportData().then(() => {
    //     this.loading = false
    //   })
    // },
    onClickShowMerchant(averageGroup) {
      this.$router.push({ name: 'aging-merchant', query: { average: averageGroup } })
    },
    onClickRefresh() {
      this.fetchReportDownloads()
    },
    getFilterIcon(key) {
      const iconMap = {
        status: 'LoaderIcon',
        'orders.created_at': 'CalendarIcon',
        destination_city_id: 'MapPinIcon',
        customer_name: 'UserIcon',
        origin_city_id: 'MapPinIcon',
        origin_warehouse: 'HomeIcon',
        destination_warehouse: 'HomeIcon',
        customer_phone: 'PhoneIcon',
        'orders.weight': 'ShoppingBagIcon',
        merchant_id: 'LayersIcon',
        merchant_business_id: 'BriefcaseIcon',
        status_date: 'CalendarIcon',
        rider: 'TruckIcon',
        'staff.id': 'TruckIcon',
        'app_warehouses.id': 'HomeIcon',
        current_warehouse: 'HomeIcon',
        'order_status_key_5.created_at': 'CalendarIcon',
        'order_status_key_9.created_at': 'CalendarIcon',
        'order_status_key_12.created_at': 'CalendarIcon',
        'osk10.created_at': 'CalendarIcon',
        'osk19.created_at': 'CalendarIcon',
        'osk20.created_at': 'CalendarIcon',
        'osk27.created_at': 'CalendarIcon',
        'order_reschedules.created_at': 'CalendarIcon',
        'orders.cod': 'FileTextIcon',
        'orders.collected_cod': 'CheckCircleIcon',
        'orders.delivery_charge': 'CreditCardIcon',
        'orders.temporary_warehouse_id': 'HomeIcon',
        walking_customer_id: 'UserCheckIcon',
        order_flag: 'FlagIcon',
        delivery_attempts: 'RotateCwIcon',
      }
      return iconMap[key] || 'FilterIcon'
    },
    getIconColor(key) {
      const colorMap = {
        status: '#39FF14', // Bright Green
        'orders.created_at': '#4285F4', // Blue
        destination_city_id: '#A855F7', // Purple
        customer_name: '#6366F1', // Indigo
        origin_city_id: '#F97316', // Orange
        origin_warehouse: '#0EA5E9', // Sky Blue
        destination_warehouse: '#65A30D', // Olive/Green
        customer_phone: '#14B8A6', // Teal
        'orders.weight': '#84CC16', // Lime
        merchant_id: '#8B5CF6', // Violet
        merchant_business_id: '#D97706', // Amber
        status_date: '#84CC16', // Lime
        rider: '#06B6D4', // Cyan
        'staff.id': '#06B6D4',
        'app_warehouses.id': '#0EA5E9',
        current_warehouse: '#65A30D',
        'order_status_key_5.created_at': '#4285F4',
        'order_status_key_9.created_at': '#4285F4',
        'order_status_key_12.created_at': '#4285F4',
        'osk10.created_at': '#4285F4',
        'osk19.created_at': '#4285F4',
        'osk20.created_at': '#4285F4',
        'osk27.created_at': '#4285F4',
        'order_reschedules.created_at': '#4285F4',
        'orders.cod': '#F87171', // Red/Salmon
        'orders.collected_cod': '#059669', // Emerald Green
        'orders.delivery_charge': '#EC4899', // Pink
        'orders.temporary_warehouse_id': '#64748B', // Slate
        walking_customer_id: '#C084FC', // Light Purple
        order_flag: '#EF4444', // Red
        delivery_attempts: '#F97316', // Orange
      }
      return colorMap[key] || '#B9B9C3'
    },
    getLabel(key) {
      return this.filterLabels[key] || key
    },
    renderFilterValue(key, value, allFilters = {}) {
      if (value === null || value === undefined || value === '') return 'N/A'

      // 1. Detect and handle comma-separated strings (like "key_2,key_9,")
      let processedValue = value
      if (typeof value === 'string' && value.includes(',')) {
        processedValue = value
          .split(',') // Split into ["key_2", "key_9", ""]
          .map(v => v.trim()) // Remove whitespace
          .filter(v => v !== '') // Remove empty strings (fixes the trailing comma issue)
      }

      // 2. Handle Arrays (Recursive mapping)
      if (Array.isArray(processedValue)) {
        return processedValue
          .map(v => this.renderFilterValue(key, v, allFilters))
          .filter(v => v !== null && v !== '')
          .join(', ')
      }

      const idInt = parseInt(processedValue, 10)
      // Lookup Logic based on Key
      switch (key) {
        case 'status':
          return this.statusOptions.find(o => o.key === processedValue)?.name || value

        case 'origin_warehouse':
        case 'destination_warehouse':
        case 'orders.temporary_warehouse_id':
        case 'app_warehouses.id':
        case 'current_warehouse':
          return this.warehouseOptions.find(o => o.id === idInt)?.name || value

        case 'origin_city_id':
        case 'destination_city_id':
          return this.cityOptions.find(o => o.id === idInt)?.name || value

        case 'merchant_id':
          return this.merchantOptions.find(o => o.id === idInt)?.name || value

        case 'merchant_business_id': {
          // Use the merchant_id from the context (3rd argument)
          const mId = allFilters.merchant_id
          if (mId && this.merchantBusinessMap[mId]) {
            const business = this.merchantBusinessMap[mId].find(o => o.id === idInt)
            return business ? business.business_name : processedValue
          }
          return processedValue
        }

        case 'rider':
        case 'staff.id': {
          const rider = this.riderOptions.find(o => o.id === idInt)
          return rider ? `${rider.first_name} ${rider.last_name}` : value
        }

        case 'walking_customer_id':
          return this.walkingCustomers.find(o => o.id === idInt)?.name || value

        case 'order_flag':
          return this.flagOptions.find(o => o.id === idInt)?.name || value

        // --- DATE FORMATTING ---
        case 'orders.created_at':
        case 'status_date':
        case 'order_status_key_5.created_at':
        case 'order_status_key_9.created_at':
        case 'order_status_key_12.created_at':
        case 'osk10.created_at':
        case 'osk19.created_at':
        case 'osk20.created_at':
        case 'osk27.created_at':
        case 'order_reschedules.created_at': {
          // Check if it's a date range (e.g., "2023-01-01 to 2023-01-31")
          if (typeof processedValue === 'string' && processedValue.includes(' to ')) {
            return processedValue
              .split(' to ')
              // eslint-disable-next-line no-underscore-dangle
              .map(datePart => this.__dateTimeFormatter(datePart.trim()))
              .join(' to ')
          }
          // Single date
          // eslint-disable-next-line no-underscore-dangle
          return this.__dateTimeFormatter(processedValue)
        }

        default:
          return value // Return raw value for weights, dates, search strings, etc.
      }
    },
    getStatusFilterEntries(rawValue) {
      if (rawValue === null || rawValue === undefined || rawValue === '') return []

      let values = rawValue

      if (typeof values === 'string' && values.includes(',')) {
        values = values
          .split(',')
          .map(v => v.trim())
          .filter(v => v !== '')
      }

      if (!Array.isArray(values)) values = [values]

      return values
        .map(v => this.statusOptions.find(o => o.key === v))
        .filter(Boolean)
    },
    parseFilters(filterData) {
      if (!filterData) return {}
      return typeof filterData === 'string' ? JSON.parse(filterData) : filterData
    },
    openStatusModal(statuses) {
      this.selectedStatusesForModal = statuses
      this.$refs.statusFilterModal.openModal()
    },
    handleChangePage(page) {
      this.page = page
      this.fetchReportDownloads()
    },
    applyFilters() {
      this.fetchReportDownloads()
    },
    clearFilters() {
      this.filters = {
        created_at: {
          startDate: null,
          endDate: null,
        },
      }
      this.$nextTick(() => {
        this.fetchReportDownloads()
      })
    },
    sortChanged(e) {
      const key = e.sortBy
      if (key) {
        const active = e.sortDesc ? '-' : ''
        this.sort = active + key
      }
    },
  },
}
</script>

<style lang="scss">
@import '../@core/scss/vue/libs/vue-select.scss';

.color-dot-container {
  display: flex;
  align-items: center;
}

.color-dot {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: inline-block;
  margin-right: 8px;
}

.timeAgo {
  margin-left: 5px;
  font-size: 13px;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
}

.refresh-button {
  float: right;
}

.refresh-icon {
  color: black !important;
}

.refresh-span {
  color: black !important;
  font-size: 13px;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  margin-bottom: 1px;
}
</style>
